// Updated AdminPanel.jsx (now with user paging)
import React, { useEffect, useState } from 'react';
import { useRole } from '../hooks/useRole';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import EditModModal from './EditModal';
import AssignModModal from './AssignModal';
import CreateModModal from './CreateModModal';

const AdminPanel = () => {
  const { isAdmin } = useRole();
  const [users, setUsers] = useState([]);
  const [mods, setMods] = useState([]);
  const [assignUser, setAssignUser] = useState(null);
  const [editMod, setEditMod] = useState(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  // Paging state
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const pageSize = 10;

  const { isLoading, getAccessTokenSilently } = useAuth0();
  if (isLoading) return <div>Loading...</div>;

  // Fetch users with paging
  const fetchUsers = async (pageNumber = 0) => {
    try {
      const token = await getAccessTokenSilently();
      const usersRes = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/users?page=${pageNumber}&size=${pageSize}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await usersRes.json();

      setUsers(data.content || []);
      setTotalPages(data.totalPages || 0);
      setPage(data.number || 0);
    } catch (err) {
      console.error("Error fetching users:", err);
    }
  };

  // Fetch mods (unchanged)
  const fetchMods = async () => {
    try {
      const token = await getAccessTokenSilently();
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/mods`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMods(res.data);
    } catch (err) {
      console.error("Error fetching mods:", err);
    }
  };

  // Initial fetch for both
  useEffect(() => {
    fetchUsers();
    fetchMods();
  }, []);

  const assignMod = async (userId, modId) => {
    const token = await getAccessTokenSilently();
    await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/assign-mod-to-user`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ userId, modId }),
    });
    fetchUsers(page); // refresh current page
  };

  const handleModEdit = async (modId, updatedData) => {
    const token = await getAccessTokenSilently();
    await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/mods/${modId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(updatedData),
    });
    fetchMods();
  };

  if (!isAdmin) return <div>Access Denied</div>;

  return (
    <div className="p-4">
      <h1 className="text-xl font-bold mb-4">Admin Panel</h1>

      {/* Users Table with Paging */}
      <div className="mb-4">
        <h2 className="font-semibold">All Users</h2>

        <div className="overflow-x-auto">
          <div className="max-h-16 overflow-y-auto border rounded">
            <table className="table-auto min-w-full text-sm">
              <thead className="bg-gray-100 sticky top-0 z-10">
                <tr>
                  <th className="p-2 text-left">Email</th>
                  <th className="p-2 text-left">Username</th>
                  <th className="p-2 text-left">Assign Mod</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-t">
                    <td className="p-2">{u.email}</td>
                    <td className="p-2">{u.username}</td>
                    <td className="p-2">
                      <button
                        className="text-blue-600 hover:underline"
                        onClick={() => setAssignUser(u)}
                      >
                        Assign
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination Controls */}
        <div className="mt-2 flex gap-2 items-center">
          <button
            disabled={page === 0}
            onClick={() => fetchUsers(page - 1)}
            className="px-2 py-1 border rounded disabled:opacity-50"
          >
            Prev
          </button>
          <span>
            Page {page + 1} of {totalPages}
          </span>
          <button
            disabled={page + 1 === totalPages}
            onClick={() => fetchUsers(page + 1)}
            className="px-2 py-1 border rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>

      {/* Mods Table */}
      <div>
        <h2 className="font-semibold">All Mods</h2>
        <button onClick={() => setCreateModalOpen(true)}>Create New Mod</button>
        <div className="overflow-x-auto mb-4">
          <table className="table-auto min-w-full text-sm">
            <thead>
              <tr>
                <th>Game</th>
                <th>Name</th>
                <th>FIFA Version</th>
                <th>Mod Version</th>
                <th>Squad Link</th>
                <th>Mod Link</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {mods.map((mod) => (
                <tr key={mod.id}>
                  <td>{mod.game}</td>
                  <td>{mod.modName}</td>
                  <td>{mod.gameVersion}</td>
                  <td>{mod.modVersion}</td>
                  <td>{mod.squadFileLink}</td>
                  <td>{mod.modLink}</td>
                  <td>
                    <button onClick={() => setEditMod(mod)} className="ml-2">
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modals */}
      {assignUser && (
        <AssignModModal
          user={assignUser}
          mods={mods}
          onClose={() => setAssignUser(null)}
          onAssign={assignMod}
        />
      )}

      {editMod && (
        <EditModModal
          mod={editMod}
          onClose={() => setEditMod(null)}
          onSave={handleModEdit}
        />
      )}

      {createModalOpen && (
        <CreateModModal
          onClose={() => setCreateModalOpen(false)}
          onCreate={fetchMods}
        />
      )}
    </div>
  );
};

export default AdminPanel;
